import React, { useState, useEffect, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { toast } from 'react-toastify';
import './CommonStyles.css';
import './Products.css';
import './AddSellStyles.css';
import apiService from '../../services/api';
import dateUtils from '../../utils/dateUtils';

import { exportToCSV, exportToExcel, exportToPDF, printPage } from './exportUtils';

const Expenses = ({ activeTab: propActiveTab }) => {
  const [activeTab, setActiveTab] = useState('list_expenses');

  useEffect(() => {
    if (propActiveTab) {
      if (propActiveTab === 'expenses') {
        setActiveTab('list_expenses');
      } else {
        setActiveTab(propActiveTab);
      }
    }
  }, [propActiveTab]);

  // Shared State
  const [searchTerm, setSearchTerm] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [showExportDropdown, setShowExportDropdown] = useState(false);

  // List State
  const [expenses, setExpenses] = useState([]);
  const [perPage, setPerPage] = useState(25);
  const [currentPage, setCurrentPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [lastPage, setLastPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [openActionId, setOpenActionId] = useState(null);
  const [dropdownPosition, setDropdownPosition] = useState({ top: null, bottom: null, left: 0, maxHeight: null });

  // View Modal State
  const [viewExpense, setViewExpense] = useState(null);
  const [showViewModal, setShowViewModal] = useState(false);

  // Edit State
  const [editingExpense, setEditingExpense] = useState(null);

  // Metadata
  const [locations, setLocations] = useState([]);
  const [expenseCategories, setExpenseCategories] = useState([]);
  const [paymentAccounts, setPaymentAccounts] = useState([]);
  const [users, setUsers] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [taxRates, setTaxRates] = useState([]);
  const [paymentMethods] = useState([
    { id: 'cash', name: 'Cash' },
    { id: 'card', name: 'Card' },
    { id: 'cheque', name: 'Cheque' },
    { id: 'bank_transfer', name: 'Bank Transfer' },
    { id: 'other', name: 'Other' }
  ]);

  // Applied Filter States (committed on Apply)
  const [filterLocation, setFilterLocation] = useState('all');
  const [filterCategory, setFilterCategory] = useState('all');
  const [filterPaymentStatus, setFilterPaymentStatus] = useState('all');
  const [filterStartDate, setFilterStartDate] = useState('');
  const [filterEndDate, setFilterEndDate] = useState('');

  // Pending filter states (in modal before Apply)
  const [pendingLocation, setPendingLocation] = useState('all');
  const [pendingCategory, setPendingCategory] = useState('all');
  const [pendingPaymentStatus, setPendingPaymentStatus] = useState('all');
  const [pendingStartDate, setPendingStartDate] = useState('');
  const [pendingEndDate, setPendingEndDate] = useState('');

  // Form State
  const getCurrentDateTime = () => dateUtils.getChicagoISOString();

  const emptyForm = () => ({
    location_id: '',
    expense_category_id: '',
    expense_sub_category_id: '',
    ref_no: '',
    transaction_date: getCurrentDateTime(),
    expense_for: '',
    contact_id: '',
    tax_id: '',
    final_total: '',
    additional_notes: '',
    is_refund: false,
    is_recurring: false,
    recur_interval: '',
    recur_interval_type: 'days',
    recur_repetitions: '',
    document: null
  });

  const [formData, setFormData] = useState(emptyForm());
  const [showPaymentSection, setShowPaymentSection] = useState(false);
  const [paymentData, setPaymentData] = useState({
    amount: 0,
    paid_on: getCurrentDateTime(),
    payment_method: 'cash',
    payment_account_id: '',
    payment_note: ''
  });

  // Category Management State
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [categoryFormData, setCategoryFormData] = useState({ name: '', code: '', description: '' });

  // Payments State
  const [showPaymentsModal, setShowPaymentsModal] = useState(false);
  const [paymentsList, setPaymentsList] = useState([]);
  const [paymentsLoading, setPaymentsLoading] = useState(false);
  const [showAddPaymentModal, setShowAddPaymentModal] = useState(false);
  const [paymentTargetExpense, setPaymentTargetExpense] = useState(null);
  const [addPaymentForm, setAddPaymentForm] = useState({
    amount: '',
    method: 'cash',
    paid_on: getCurrentDateTime(),
    payment_account_id: '',
    note: '',
    card_number: '',
    card_holder_name: '',
    card_type: '',
    card_month: '',
    card_year: '',
    card_security: '',
    cheque_number: '',
    bank_account_number: ''
  });

  // Document Preview State
  const [showDocModal, setShowDocModal] = useState(false);
  const [docUrl, setDocUrl] = useState('');
  const [docIsImage, setDocIsImage] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // ─── Fetch expenses list ───────────────────────────────────────────────────
  const fetchExpenses = useCallback(async (page = currentPage) => {
    setLoading(true);
    try {
      const params = {
        page,
        per_page: perPage,
        search: searchTerm || undefined,
        location_id: filterLocation !== 'all' ? filterLocation : undefined,
        expense_category_id: filterCategory !== 'all' ? filterCategory : undefined,
        payment_status: filterPaymentStatus !== 'all' ? filterPaymentStatus : undefined,
        start_date: filterStartDate || undefined,
        end_date: filterEndDate || undefined,
      };
      const res = await apiService.getExpenses(params);
      console.log('[Expenses API] raw response:', res);
      // Handle DataTables response shape: { draw, recordsTotal, recordsFiltered, data: [...] }
      const expenseList = Array.isArray(res.data) ? res.data : (Array.isArray(res) ? res : []);
      const totalRecords = res.recordsTotal ?? res.recordsFiltered ?? res.total ?? expenseList.length;
      const recordsPerPage = perPage;
      const calculatedLastPage = Math.max(1, Math.ceil(totalRecords / recordsPerPage));
      setExpenses(expenseList);
      setTotal(totalRecords);
      setLastPage(calculatedLastPage);
      setCurrentPage(page);
    } catch (error) {
      console.error('Failed to fetch expenses:', error);
    } finally {
      setLoading(false);
    }
  }, [perPage, searchTerm, filterLocation, filterCategory, filterPaymentStatus, filterStartDate, filterEndDate]);

  // Fetch metadata on mount
  useEffect(() => {
    fetchMetadata();
  }, []);

  // Fetch expenses when list tab is active or dependencies change
  useEffect(() => {
    if (activeTab === 'list_expenses') {
      fetchExpenses(1);
    }
  }, [activeTab, perPage, searchTerm, filterLocation, filterCategory, filterPaymentStatus, filterStartDate, filterEndDate]);

  // Close dropdown on outside click
  useEffect(() => {
    if (!openActionId) return;
    const handleClickOutside = (e) => {
      if (e.target.closest('.action-btn-trigger') || e.target.closest('.action-dropdown-menu')) return;
      setOpenActionId(null);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [openActionId]);

  const normalizeArray = (data, field = 'data') => {
    if (Array.isArray(data)) return data;
    if (data && Array.isArray(data[field])) return data[field];
    if (data && Array.isArray(data.data)) return data.data;
    // Handle paginated Laravel responses: { data: { data: [...], current_page, ... } }
    if (data && data.data && Array.isArray(data.data.data)) return data.data.data;
    if (data && data.data && data.data.data && Array.isArray(data.data.data.data)) return data.data.data.data;
    return [];
  };

  const getDisplayName = (val) => {
    if (val == null) return '';
    if (typeof val === 'string') return val;
    if (typeof val === 'object') {
      return val.name?.trim()
        || val.full_name?.trim()
        || `${val.first_name || ''} ${val.last_name || ''}`.trim()
        || val.username?.trim()
        || val.email?.trim()
        || '';
    }
    return String(val);
  };

  const getId = (val) => {
    if (val == null) return '';
    if (typeof val === 'number') return String(val);
    if (typeof val === 'string') return val;
    if (typeof val === 'object' && val.id != null) return String(val.id);
    return String(val);
  };

  const fetchMetadata = async () => {
    const fetchOne = async (fn, label) => {
      try {
        const res = await fn();
        console.log(`[Metadata] ${label} raw:`, res);
        return res;
      } catch (err) {
        console.error(`[Metadata] ${label} failed:`, err.message);
        return null;
      }
    };

    const locationsData = await fetchOne(apiService.getBusinessLocations.bind(apiService), 'locations');
    const categoriesData = await fetchOne(apiService.getExpenseCategories.bind(apiService), 'categories');
    const accountsData = await fetchOne(apiService.getPaymentAccounts.bind(apiService), 'accounts');
    const usersData = await fetchOne(apiService.getUsers.bind(apiService, { per_page: -1 }), 'users');
    const contactsData = await fetchOne(apiService.getContacts.bind(apiService), 'contacts');
    const taxRatesData = await fetchOne(apiService.getTaxRates.bind(apiService), 'taxRates');

    setLocations(normalizeArray(locationsData));
    setExpenseCategories(normalizeArray(categoriesData));
    setPaymentAccounts(normalizeArray(accountsData));
    setUsers(normalizeArray(usersData));
    setContacts(normalizeArray(contactsData));
    setTaxRates(normalizeArray(taxRatesData));
  };

  // Compute tax amount for display only (backend calculates it from tax_id)
  const selectedTaxRate = taxRates.find(t => t.id === parseInt(formData.tax_id));
  const taxPercent = selectedTaxRate ? parseFloat(selectedTaxRate.amount) : 0;
  const totalBeforeTax = parseFloat(formData.final_total) || 0;
  const taxAmountDisplay = taxPercent > 0 ? (totalBeforeTax * taxPercent / (100 + taxPercent)) : 0;
  const paymentDue = totalBeforeTax - (parseFloat(paymentData.amount) || 0);

  // ─── Export ────────────────────────────────────────────────────────────────
  const handleExport = (format) => {
    if (!expenses || expenses.length === 0) {
      alert('No data to export');
      setShowExportDropdown(false);
      return;
    }
    const headers = ['Date', 'Reference No', 'Expense Category', 'Location', 'Payment Status', 'Total Amount', 'Expense For', 'Added By'];
    const rows = expenses.map(e => [
      e.date || '',
      e.ref_no || '',
      e.category || '',
      e.location || '',
      e.payment_status || '',
      e.total_amount || '0.00',
      e.expense_for || '',
      e.added_by || ''
    ]);
    const title = 'Expenses List';
    const filename = 'expenses';
    if (format === 'CSV') exportToCSV(headers, rows, filename);
    else if (format === 'Excel') exportToExcel(headers, rows, filename);
    else if (format === 'PDF') exportToPDF(title, headers, rows, filename);
    else if (format === 'Print') printPage();
    setShowExportDropdown(false);
  };

  // ─── Add / Edit form handlers ──────────────────────────────────────────────
  const handleAddClick = () => {
    setEditingExpense(null);
    setFormData(emptyForm());
    setPaymentData({ amount: 0, paid_on: getCurrentDateTime(), payment_method: 'cash', payment_account_id: '', payment_note: '' });
    setShowPaymentSection(false);
    setActiveTab('add_expense');
  };

  const handleEditClick = (expense) => {
    console.log('[EditClick] expense object:', expense);
    console.log('[EditClick] expense.contact_id raw:', expense.contact_id);
    console.log('[EditClick] expense.contact_id after getId:', getId(expense.contact_id));
    setEditingExpense(expense);
    setFormData({
      location_id: expense.location_id || '',
      expense_category_id: expense.expense_category_id || '',
      expense_sub_category_id: expense.expense_sub_category_id || '',
      ref_no: expense.ref_no || '',
      transaction_date: expense.transaction_date || expense.date || getCurrentDateTime(),
      expense_for: getId(expense.expense_for) || '',
      contact_id: getId(expense.contact_id) || '',
      tax_id: expense.tax_id || '',
      final_total: expense.final_total || '',
      additional_notes: expense.additional_notes || '',
      is_refund: expense.is_refund || false,
      is_recurring: expense.is_recurring || false,
      recur_interval: expense.recur_interval || '',
      recur_interval_type: expense.recur_interval_type || 'days',
      recur_repetitions: expense.recur_repetitions || '',
      document: null
    });
    setShowPaymentSection(false);
    setOpenActionId(null);
    setActiveTab('add_expense');
  };

  const handleCancelAdd = () => {
    setEditingExpense(null);
    setFormData(emptyForm());
    setShowPaymentSection(false);
    setPaymentData({ amount: 0, paid_on: getCurrentDateTime(), payment_method: 'cash', payment_account_id: '', payment_note: '' });
    setActiveTab('list_expenses');
  };

  const handleSaveExpense = async () => {
    if (isSaving) return;
    setIsSaving(true);
    try {
      const payload = { ...formData };

      // Normalize transaction_date: YYYY-MM-DDTHH:mm → YYYY-MM-DD HH:mm:ss (MySQL format)
      if (payload.transaction_date) {
        payload.transaction_date = dateUtils.formatUTCForApi(payload.transaction_date);
      }

      // Attach payment if user has filled the payment section
      if (showPaymentSection && parseFloat(paymentData.amount) > 0) {
        const paymentLine = {
          amount: parseFloat(paymentData.amount),
          method: paymentData.payment_method,
          note: paymentData.payment_note || ''
        };
        if (paymentData.payment_account_id) {
          paymentLine.account_id = paymentData.payment_account_id;
        }
        payload.payment = [paymentLine];
        console.log('[Expenses] payment payload:', paymentLine);
      }
      console.log('[Expenses] full save payload:', payload);

      if (editingExpense) {
        await apiService.updateExpense(editingExpense.id, payload);
        toast.success('Expense updated successfully!');
      } else {
        await apiService.createExpense(payload);
        toast.success('Expense created successfully!');
      }
      handleCancelAdd();
      fetchExpenses(1);
    } catch (error) {
      toast.error('Failed to save expense: ' + error.message);
    } finally {
      setIsSaving(false);
    }
  };

  // ─── Delete ────────────────────────────────────────────────────────────────
  const handleDeleteExpense = async (expense) => {
    if (!window.confirm(`Delete expense "${expense.ref_no || expense.id}"? This cannot be undone.`)) return;
    setOpenActionId(null);
    try {
      await apiService.deleteExpense(expense.id);
      toast.success('Expense deleted successfully!');
      fetchExpenses(currentPage);
    } catch (error) {
      toast.error('Failed to delete expense: ' + error.message);
    }
  };

  // ─── View ──────────────────────────────────────────────────────────────────
  const handleViewExpense = (expense) => {
    setViewExpense(expense);
    setShowViewModal(true);
    setOpenActionId(null);
  };

  // ─── View Payments ─────────────────────────────────────────────────────────
  const handleViewPayments = async (expense) => {
    setOpenActionId(null);
    setPaymentTargetExpense(expense);
    setShowPaymentsModal(true);
    setPaymentsLoading(true);
    try {
      const res = await apiService.getPayments(expense.id);
      const list = Array.isArray(res.data) ? res.data : (Array.isArray(res) ? res : []);
      setPaymentsList(list);
    } catch (error) {
      console.error('Failed to fetch payments:', error);
      alert('Failed to load payments: ' + error.message);
      setPaymentsList([]);
    } finally {
      setPaymentsLoading(false);
    }
  };

  // ─── Add Payment ───────────────────────────────────────────────────────────
  const handleOpenAddPayment = (expense) => {
    setPaymentTargetExpense(expense);
    const due = parseFloat(expense.final_total || expense.total_amount || 0) - parseFloat(expense.amount_paid || 0);
    setAddPaymentForm({
      amount: due > 0 ? due.toFixed(2) : '',
      method: 'cash',
      paid_on: getCurrentDateTime(),
      payment_account_id: '',
      note: '',
      card_number: '',
      card_holder_name: '',
      card_type: '',
      card_month: '',
      card_year: '',
      card_security: '',
      cheque_number: '',
      bank_account_number: ''
    });
    setShowAddPaymentModal(true);
    setOpenActionId(null);
  };

  const handleSavePayment = async () => {
    if (!paymentTargetExpense || isSaving) return;
    setIsSaving(true);
    try {
      const payload = {
        transaction_id: paymentTargetExpense.id,
        amount: parseFloat(addPaymentForm.amount) || 0,
        method: addPaymentForm.method,
        paid_on: addPaymentForm.paid_on,
        payment_account_id: addPaymentForm.payment_account_id || undefined,
        note: addPaymentForm.note,
        card_number: addPaymentForm.card_number || undefined,
        card_holder_name: addPaymentForm.card_holder_name || undefined,
        card_type: addPaymentForm.card_type || undefined,
        card_month: addPaymentForm.card_month || undefined,
        card_year: addPaymentForm.card_year || undefined,
        card_security: addPaymentForm.card_security || undefined,
        cheque_number: addPaymentForm.cheque_number || undefined,
        bank_account_number: addPaymentForm.bank_account_number || undefined
      };
      await apiService.addPayment(payload);
      toast.success('Payment added successfully!');
      setShowAddPaymentModal(false);
      fetchExpenses(currentPage);
    } catch (error) {
      toast.error('Failed to add payment: ' + error.message);
    } finally {
      setIsSaving(false);
    }
  };

  // ─── Document handlers ─────────────────────────────────────────────────────
  const handleDownloadDocument = (expense) => {
    setOpenActionId(null);
    if (!expense.document) return;
    const link = document.createElement('a');
    link.href = `${window.location.origin}/uploads/documents/${expense.document}`;
    link.download = expense.document;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleViewDocument = (expense) => {
    setOpenActionId(null);
    if (!expense.document) return;
    const url = `${window.location.origin}/uploads/documents/${expense.document}`;
    const imageExts = ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp'];
    const ext = expense.document.split('.').pop().toLowerCase();
    setDocUrl(url);
    setDocIsImage(imageExts.includes(ext));
    setShowDocModal(true);
  };

  // ─── Filters ───────────────────────────────────────────────────────────────
  const handleOpenFilters = () => {
    setPendingLocation(filterLocation);
    setPendingCategory(filterCategory);
    setPendingPaymentStatus(filterPaymentStatus);
    setPendingStartDate(filterStartDate);
    setPendingEndDate(filterEndDate);
    setShowFilters(true);
  };

  const handleApplyFilters = () => {
    setFilterLocation(pendingLocation);
    setFilterCategory(pendingCategory);
    setFilterPaymentStatus(pendingPaymentStatus);
    setFilterStartDate(pendingStartDate);
    setFilterEndDate(pendingEndDate);
    setCurrentPage(1);
    setShowFilters(false);
  };

  const handleClearFilters = () => {
    setPendingLocation('all');
    setPendingCategory('all');
    setPendingPaymentStatus('all');
    setPendingStartDate('');
    setPendingEndDate('');
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    setFormData({ ...formData, document: file });
  };

  // ─── Category Management ───────────────────────────────────────────────────
  const handleAddCategory = () => {
    setEditingCategory(null);
    setCategoryFormData({ name: '', code: '', description: '' });
    setShowCategoryModal(true);
  };

  const handleEditCategory = (category) => {
    setEditingCategory(category);
    setCategoryFormData({ name: category.name, code: category.code || '', description: category.description || '' });
    setShowCategoryModal(true);
  };

  const handleSaveCategory = async () => {
    if (isSaving) return;
    setIsSaving(true);
    try {
      if (editingCategory) {
        await apiService.updateExpenseCategory(editingCategory.id, categoryFormData);
        toast.success('Category updated successfully!');
      } else {
        await apiService.createExpenseCategory(categoryFormData);
        toast.success('Category created successfully!');
      }
      setShowCategoryModal(false);
      fetchMetadata();
    } catch (error) {
      toast.error('Failed to save category: ' + error.message);
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteCategory = async (categoryId) => {
    if (!window.confirm('Are you sure you want to delete this category?')) return;
    try {
      await apiService.deleteExpenseCategory(categoryId);
      toast.success('Category deleted successfully!');
      fetchMetadata();
    } catch (error) {
      toast.error('Failed to delete category: ' + error.message);
    }
  };

  // ─── Pagination ────────────────────────────────────────────────────────────
  const handlePageChange = (page) => {
    if (page < 1 || page > lastPage) return;
    fetchExpenses(page);
  };

  const fromEntry = total === 0 ? 0 : (currentPage - 1) * perPage + 1;
  const toEntry = Math.min(currentPage * perPage, total);

  // ─── Payment status badge ──────────────────────────────────────────────────
  const statusBadge = (status) => {
    const map = { paid: '#16a34a', due: '#dc2626', partial: '#d97706' };
    return (
      <span style={{ background: map[status] || '#6b7280', color: 'white', padding: '0.2rem 0.6rem', borderRadius: '12px', fontSize: '0.75rem', fontWeight: '600', textTransform: 'capitalize' }}>
        {status || 'N/A'}
      </span>
    );
  };

  // RENDER ADD EXPENSE FORM
  if (activeTab === 'add_expense') {
    return (
      <div className="sell-form-container">
        <div className="page-header">
          <button
            onClick={handleCancelAdd}
            style={{
              background: 'white',
              border: '1px solid #e2e8f0',
              borderRadius: '8px',
              padding: '0.5rem 0.75rem',
              cursor: 'pointer',
              fontSize: '1.2rem',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              minWidth: '40px',
              height: '40px'
            }}
          >
            ←
          </button>
          <h2 style={{ fontSize: '1.25rem' }}><i className={`fa fa-${editingExpense ? 'edit' : 'plus'}`}></i> {editingExpense ? 'Edit Expense' : 'Add Expense'}</h2>
        </div>

        <form className="sell-form" onSubmit={(e) => { e.preventDefault(); handleSaveExpense(); }}>

          {/* Basic Information Section */}
          <div className="form-section">
            <h3 className="section-title">Basic Information</h3>
            <div className="form-row" style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '1.5rem' }}>
              <div className="form-group">
                <label>Business Location: *</label>
                <select
                  className="form-select"
                  value={formData.location_id}
                  onChange={(e) => setFormData({ ...formData, location_id: e.target.value })}
                  required
                >
                  <option value="">Select Location</option>
                  {Array.isArray(locations) && locations.map(loc => (
                    <option key={loc.id} value={loc.id}>{loc.name}</option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label>Expense Category:</label>
                <select
                  className="form-select"
                  value={formData.expense_category_id}
                  onChange={(e) => setFormData({ ...formData, expense_category_id: e.target.value })}
                >
                  <option value="">Select Category</option>
                  {Array.isArray(expenseCategories) && expenseCategories.map(cat => (
                    <option key={cat.id} value={cat.id}>{cat.name}</option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label>Sub category:</label>
                <select
                  className="form-select"
                  value={formData.expense_sub_category_id}
                  onChange={(e) => setFormData({ ...formData, expense_sub_category_id: e.target.value })}
                >
                  <option value="">Select Sub Category</option>
                  {Array.isArray(expenseCategories) && expenseCategories.filter(c => c.parent_id === parseInt(formData.expense_category_id)).map(cat => (
                    <option key={cat.id} value={cat.id}>{cat.name}</option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label>Reference No:</label>
                <input
                  type="text"
                  className="form-input"
                  value={formData.ref_no}
                  onChange={(e) => setFormData({ ...formData, ref_no: e.target.value })}
                  placeholder="Leave empty to autogenerate"
                />
              </div>

              <div className="form-group">
                <label>Date: *</label>
                <input
                  type="datetime-local"
                  className="form-input"
                  value={formData.transaction_date}
                  onChange={(e) => setFormData({ ...formData, transaction_date: e.target.value })}
                  required
                />
              </div>

              <div className="form-group">
                <label>Expense for (User):</label>
                <select
                  className="form-select"
                  value={formData.expense_for}
                  onChange={(e) => setFormData({ ...formData, expense_for: e.target.value })}
                >
                  <option value="">None</option>
                  {Array.isArray(users) && users
                    .map(user => {
                      const displayName = user.name?.trim()
                        || `${user.first_name || ''} ${user.last_name || ''}`.trim()
                        || user.username?.trim()
                        || user.email?.trim()
                        || 'Unknown';
                      return { ...user, displayName };
                    })
                    .filter(user => user.displayName && user.displayName !== 'Unknown')
                    .map(user => (
                      <option key={user.id} value={String(user.id)}>{user.displayName}</option>
                    ))}
                </select>
              </div>

              <div className="form-group">
                <label>Expense for contact:</label>
                <select
                  className="form-select"
                  value={formData.contact_id}
                  onChange={(e) => setFormData({ ...formData, contact_id: e.target.value })}
                >
                  <option value="">Please Select</option>
                  {Array.isArray(contacts) && contacts
                    .filter(contact => contact.name && String(contact.name).trim())
                    .map(contact => (
                      <option key={contact.id} value={String(contact.id)}>{String(contact.name).trim()}</option>
                    ))}
                </select>
              </div>

              <div className="form-group">
                <label>Attach Document:</label>
                <input
                  type="file"
                  className="form-input"
                  onChange={handleFileChange}
                  accept=".pdf,.csv,.zip,.doc,.docx,.jpeg,.jpg,.png"
                />
                <small style={{ fontSize: '0.75rem', color: '#64748b', marginTop: '0.25rem', display: 'block' }}>
                  Max File size: 5MB<br />
                  Allowed File: .pdf, .csv, .zip, .doc, .docx, .jpeg, .jpg, .png
                </small>
              </div>
            </div>
          </div>

          {/* Expense Details Section */}
          <div className="form-section">
            <h3 className="section-title">Expense Details</h3>
            <div className="form-row" style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '1.5rem' }}>
              <div className="form-group">
                <label>Applicable Tax:</label>
                <select
                  className="form-select"
                  value={formData.tax_id}
                  onChange={(e) => setFormData({ ...formData, tax_id: e.target.value })}
                >
                  <option value="">None</option>
                  {Array.isArray(taxRates) && taxRates.map(tax => (
                    <option key={tax.id} value={tax.id}>{tax.name} ({tax.amount}%)</option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label>Total amount (incl. tax): *</label>
                <input
                  type="number"
                  className="form-input"
                  value={formData.final_total}
                  onChange={(e) => setFormData({ ...formData, final_total: e.target.value })}
                  placeholder="Enter total amount"
                  step="0.01"
                  min="0"
                  required
                />
              </div>

              <div className="form-group">
                <label>Tax Amount (calc.):</label>
                <input
                  type="number"
                  className="form-input"
                  value={taxAmountDisplay.toFixed(2)}
                  readOnly
                  style={{ backgroundColor: '#f8fafc' }}
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group full-width">
                <label>Additional Notes:</label>
                <textarea
                  className="form-textarea"
                  value={formData.additional_notes}
                  onChange={(e) => setFormData({ ...formData, additional_notes: e.target.value })}
                  rows="3"
                  placeholder="Enter expense notes"
                ></textarea>
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={formData.is_refund}
                    onChange={(e) => setFormData({ ...formData, is_refund: e.target.checked })}
                  />
                  Is refund?
                </label>
              </div>

              <div className="form-group">
                <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={formData.is_recurring}
                    onChange={(e) => setFormData({ ...formData, is_recurring: e.target.checked })}
                  />
                  Is Recurring?
                </label>
              </div>
            </div>
          </div>

          {/* Recurring Settings Section (Conditional) */}
          {formData.is_recurring && (
            <div className="form-section">
              <h3 className="section-title">Recurring Settings</h3>
              <div className="form-row">
                <div className="form-group">
                  <label>Recurring interval: *</label>
                  <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                    <input
                      type="number"
                      className="form-input"
                      value={formData.recur_interval}
                      onChange={(e) => setFormData({ ...formData, recur_interval: e.target.value })}
                      placeholder="Enter interval"
                      style={{ flex: 1 }}
                      required={formData.is_recurring}
                    />
                    <select
                      className="form-select"
                      value={formData.recur_interval_type}
                      onChange={(e) => setFormData({ ...formData, recur_interval_type: e.target.value })}
                      style={{ minWidth: '100px', flex: '0 0 auto' }}
                    >
                      <option value="days">Days</option>
                      <option value="weeks">Weeks</option>
                      <option value="months">Months</option>
                      <option value="years">Years</option>
                    </select>
                  </div>
                </div>

                <div className="form-group">
                  <label>No. of Repetitions:</label>
                  <input
                    type="number"
                    className="form-input"
                    value={formData.recur_repetitions}
                    onChange={(e) => setFormData({ ...formData, recur_repetitions: e.target.value })}
                    placeholder="Leave blank for infinite"
                  />
                  <small style={{ fontSize: '0.75rem', color: '#64748b', marginTop: '0.25rem', display: 'block' }}>
                    If blank expense will be generated infinite times
                  </small>
                </div>
              </div>
            </div>
          )}

          {/* Payment Section Toggle */}
          <div className="form-section">
            <button
              type="button"
              onClick={() => setShowPaymentSection(!showPaymentSection)}
              style={{
                marginBottom: '1rem',
                padding: '0.5rem 1.25rem',
                background: '#2563eb',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer',
                fontWeight: '500',
                fontSize: '0.9rem',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '0.4rem',
                whiteSpace: 'nowrap'
              }}
            >
              <i className={`fa fa-${showPaymentSection ? 'minus' : 'plus'}`}></i>
              {showPaymentSection ? 'Hide Payment' : 'Add payment'}
            </button>

            {/* Payment Information Section (Conditional) */}
            {showPaymentSection && (
              <>
                <h3 className="section-title">Payment Information</h3>
                <div className="form-row">
                  <div className="form-group">
                    <label>Amount: *</label>
                    <input
                      type="number"
                      className="form-input"
                      value={paymentData.amount}
                      onChange={(e) => setPaymentData({ ...paymentData, amount: e.target.value })}
                      placeholder="0.00"
                      step="0.01"
                      required={showPaymentSection}
                    />
                  </div>

                  <div className="form-group">
                    <label>Paid on: *</label>
                    <input
                      type="datetime-local"
                      className="form-input"
                      value={paymentData.paid_on}
                      onChange={(e) => setPaymentData({ ...paymentData, paid_on: e.target.value })}
                      required={showPaymentSection}
                    />
                  </div>

                  <div className="form-group">
                    <label>Payment Method: *</label>
                    <select
                      className="form-select"
                      value={paymentData.payment_method}
                      onChange={(e) => setPaymentData({ ...paymentData, payment_method: e.target.value })}
                      required={showPaymentSection}
                    >
                      {paymentMethods.map(method => (
                        <option key={method.id} value={method.id}>{method.name}</option>
                      ))}
                    </select>
                  </div>

                  <div className="form-group">
                    <label>Payment Account:</label>
                    <select
                      className="form-select"
                      value={paymentData.payment_account_id}
                      onChange={(e) => setPaymentData({ ...paymentData, payment_account_id: e.target.value })}
                    >
                      <option value="">Select Account</option>
                      {Array.isArray(paymentAccounts) && paymentAccounts.map(acc => (
                        <option key={acc.id} value={acc.id}>{acc.name}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group full-width">
                    <label>Payment note:</label>
                    <textarea
                      className="form-textarea"
                      value={paymentData.payment_note}
                      onChange={(e) => setPaymentData({ ...paymentData, payment_note: e.target.value })}
                      rows="2"
                      placeholder="Enter payment notes"
                    ></textarea>
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>Payment due:</label>
                    <input
                      type="number"
                      className="form-input"
                      value={paymentDue.toFixed(2)}
                      readOnly
                      style={{ backgroundColor: '#fef3c7', fontWeight: 'bold', color: '#92400e' }}
                    />
                  </div>
                </div>
              </>
            )}
          </div>

          {/* Action Buttons */}
          <div className="action-buttons-section" style={{ display: 'flex', justifyContent: 'center', gap: '1rem', marginTop: '2rem', padding: '0.5rem 0' }}>
            <button type="submit" className="save-btn" disabled={isSaving}>
              {isSaving ? '⏳ Saving...' : '💾 Save Expense'}
            </button>
            <button type="button" className="save-print-btn" onClick={handleCancelAdd} disabled={isSaving}>
              ✕ Cancel
            </button>
          </div>
        </form>
      </div>
    );
  }

  // RENDER EXPENSE CATEGORIES TAB
  if (activeTab === 'expense_categories') {
    return (
      <div className="products-container">
        <div className="main-card">


          <div className="table-controls">
            <div className="table-controls-left">
              <div className="entries-dropdown">
                <span>Show</span>
                <select value={perPage} onChange={(e) => { setPerPage(Number(e.target.value)); setCurrentPage(1); }}>
                  <option value={25}>25</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                </select>
                <span>entries</span>
              </div>
            </div>
            <div className="table-controls-right">
              <div className="tabs-search-wrapper" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <input
                  type="text"
                  placeholder="Search categories..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="tabs-search-input"
                  style={{ padding: '0.4rem 0.8rem', borderRadius: '6px', border: '1px solid #e2e8f0', outline: 'none', fontSize: '0.9rem' }}
                />
              </div>
              <button className="control-btn-primary" onClick={handleAddCategory}>
                <span className="plus-icon">+</span> Add
              </button>
            </div>
          </div>

          <div className="table-content-card">
            <div className="products-table-container">
              <table className="products-table">
                <thead>
                  <tr>
                    <th>Action</th>
                    <th>Category Name</th>
                    <th>Category Code</th>
                    <th>Description</th>
                  </tr>
                </thead>
                <tbody>
                  {!Array.isArray(expenseCategories) || expenseCategories.length === 0 ? (
                    <tr><td colSpan="4" style={{ textAlign: 'center', padding: '2rem' }}>No categories found.</td></tr>
                  ) : (
                    expenseCategories
                      .filter(cat => cat.name.toLowerCase().includes(searchTerm.toLowerCase()))
                      .map(category => (
                        <tr key={category.id}>
                          <td className="actions-cell">
                            <div className="action-dropdown-container">
                              <button
                                className="action-btn-trigger"
                                onClick={() => setOpenActionId(openActionId === category.id ? null : category.id)}
                              >
                                Actions <span className="arrow-down">▼</span>
                              </button>
                              {openActionId === category.id && (
                                <div className="action-dropdown-menu">
                                  <button onClick={() => handleEditCategory(category)}>Edit</button>
                                  <button onClick={() => handleDeleteCategory(category.id)}>Delete</button>
                                </div>
                              )}
                            </div>
                          </td>
                          <td>{category.name}</td>
                          <td>{category.code || 'N/A'}</td>
                          <td>{category.description || 'N/A'}</td>
                        </tr>
                      ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* Category Add/Edit Modal */}
        {showCategoryModal && (
          <div className="modal-overlay" onClick={() => setShowCategoryModal(false)}>
            <div className="modal-container" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '500px', width: '95%' }}>
              <div className="modal-header">
                <h2>{editingCategory ? 'Edit Category' : 'Add Category'}</h2>
                <button className="modal-close-btn" onClick={() => setShowCategoryModal(false)}>✕</button>
              </div>
              <div className="modal-content" style={{ padding: '1.5rem', maxWidth: 'none' }}>
                <div className="form-group" style={{ marginBottom: '1rem' }}>
                  <label>Category Name *</label>
                  <input
                    type="text"
                    className="form-input"
                    value={categoryFormData.name}
                    onChange={(e) => setCategoryFormData({ ...categoryFormData, name: e.target.value })}
                  />
                </div>
                <div className="form-group" style={{ marginBottom: '1rem' }}>
                  <label>Category Code</label>
                  <input
                    type="text"
                    className="form-input"
                    value={categoryFormData.code}
                    onChange={(e) => setCategoryFormData({ ...categoryFormData, code: e.target.value })}
                  />
                </div>
                <div className="form-group">
                  <label>Description</label>
                  <textarea
                    className="form-textarea"
                    value={categoryFormData.description}
                    onChange={(e) => setCategoryFormData({ ...categoryFormData, description: e.target.value })}
                    rows="3"
                  ></textarea>
                </div>
              </div>
              <div className="modal-footer">
                <button className="modal-btn close-btn" onClick={() => setShowCategoryModal(false)} disabled={isSaving}>Cancel</button>
                <button className="modal-btn primary-btn" onClick={handleSaveCategory} disabled={isSaving}>
                  {isSaving ? '⏳ Saving...' : 'Save'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  // RENDER LIST EXPENSES TAB (DEFAULT)
  return (
    <div className="products-container">
      <div className="main-card">
        <div className="table-controls">
          <div className="table-controls-left">
            <div className="entries-dropdown">
              <span>Show</span>
              <select value={perPage} onChange={(e) => { setPerPage(Number(e.target.value)); setCurrentPage(1); }}>
                <option value={25}>25</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
              <span>entries</span>
            </div>
          </div>
          <div className="table-controls-right">
            <div className="tabs-search-wrapper">
              <input
                type="text"
                placeholder="Search..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="tabs-search-input"
              />
              <button className="filter-toggle-btn" onClick={handleOpenFilters} title="Open Filters">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M10 18h4v-2h-4v2zM3 6v2h18V6H3zm3 7h12v-2H6v2z" />
                </svg>
                Filters
              </button>
            </div>
            <div className="export-dropdown-container" style={{ position: 'relative' }}>
              <button className="export-btn" onClick={() => setShowExportDropdown(!showExportDropdown)}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                  <polyline points="7 10 12 15 17 10"></polyline>
                  <line x1="12" y1="15" x2="12" y2="3"></line>
                </svg>
                Export ▾
              </button>
              {showExportDropdown && (
                <div className="export-dropdown-menu">
                  <button onClick={() => handleExport('CSV')}><i className="fa fa-file-csv"></i> Export CSV</button>
                  <button onClick={() => handleExport('Excel')}><i className="fa fa-file-excel"></i> Export Excel</button>
                  <button onClick={() => handleExport('Print')}><i className="fa fa-print"></i> Print</button>
                  <button onClick={() => handleExport('PDF')}><i className="fa fa-file-pdf"></i> Export PDF</button>
                </div>
              )}
            </div>
            <button className="control-btn-primary" onClick={handleAddClick}>
              <span className="plus-icon">+</span> Add
            </button>
          </div>
        </div>

        <div className="products-table-container">
            <table className="products-table">
              <thead>
                <tr>
                  <th>Action</th>
                  <th>Date</th>
                  <th>Reference No</th>
                  <th>Expense Category</th>
                  <th>Location</th>
                  <th>Payment Status</th>
                  <th>Total Amount</th>
                  <th>Expense For</th>
                  <th>Added By</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan="9" style={{ textAlign: 'center', padding: '2rem' }}>Loading...</td></tr>
                ) : !Array.isArray(expenses) || expenses.length === 0 ? (
                  <tr><td colSpan="9" style={{ textAlign: 'center', padding: '2rem', color: '#64748b' }}>No expenses found.</td></tr>
                ) : (
                  expenses.map(expense => (
                    <tr key={expense.id}>
                      <td className="actions-cell">
                        <div className="action-dropdown-container">
                          <button
                            className="action-btn-trigger"
                            onClick={(e) => {
                              e.stopPropagation();
                              if (openActionId === expense.id) {
                                setOpenActionId(null);
                              } else {
                                const rect = e.currentTarget.getBoundingClientRect();
                                const gap = 8;
                                const viewportHeight = window.innerHeight;
                                const viewportWidth = window.innerWidth;
                                const menuMinWidth = 220;
                                const edgePadding = 8;
                                const spaceBelow = viewportHeight - rect.bottom;
                                const spaceAbove = rect.top;
                                const openUpwards = spaceBelow < 240 && spaceAbove > spaceBelow;
                                const availableHeight = openUpwards ? (spaceAbove - gap - edgePadding) : (spaceBelow - gap - edgePadding);
                                const maxHeight = Math.max(160, Math.min(Math.floor(viewportHeight * 0.6), Math.floor(availableHeight)));
                                const left = Math.min(Math.max(rect.left, edgePadding), Math.max(edgePadding, viewportWidth - menuMinWidth - edgePadding));
                                setDropdownPosition({
                                  top: openUpwards ? null : (rect.bottom + gap),
                                  bottom: openUpwards ? (viewportHeight - rect.top + gap) : null,
                                  left,
                                  maxHeight
                                });
                                setOpenActionId(expense.id);
                              }
                            }}
                          >
                            Actions <span className="arrow-down">▼</span>
                          </button>
                          {openActionId === expense.id && ReactDOM.createPortal(
                            <div
                              className="action-dropdown-menu"
                              style={{
                                position: 'fixed',
                                top: dropdownPosition.top === null ? 'auto' : `${dropdownPosition.top}px`,
                                bottom: dropdownPosition.bottom === null ? 'auto' : `${dropdownPosition.bottom}px`,
                                left: `${dropdownPosition.left}px`,
                                maxHeight: dropdownPosition.maxHeight === null ? undefined : `${dropdownPosition.maxHeight}px`,
                                zIndex: 9999999
                              }}
                            >
                              <button className="dropdown-item" onClick={() => { setOpenActionId(null); handleViewExpense(expense); }}><i className="fa fa-eye" style={{ marginRight: '6px' }}></i>View</button>
                              <button className="dropdown-item" onClick={() => { setOpenActionId(null); handleEditClick(expense); }}><i className="fa fa-edit" style={{ marginRight: '6px' }}></i>Edit</button>
                              {expense.document && (
                                <>
                                  <button className="dropdown-item" onClick={() => { setOpenActionId(null); handleDownloadDocument(expense); }}><i className="fa fa-download" style={{ marginRight: '6px' }}></i>Download Document</button>
                                  <button className="dropdown-item" onClick={() => { setOpenActionId(null); handleViewDocument(expense); }}><i className="fa fa-file-image" style={{ marginRight: '6px' }}></i>View Document</button>
                                </>
                              )}
                              <button className="dropdown-item" onClick={() => { setOpenActionId(null); handleViewPayments(expense); }}><i className="fa fa-money-bill-alt" style={{ marginRight: '6px' }}></i>View Payments</button>
                              {expense.payment_status !== 'paid' && (
                                <button className="dropdown-item" onClick={() => { setOpenActionId(null); handleOpenAddPayment(expense); }}><i className="fa fa-plus-circle" style={{ marginRight: '6px' }}></i>Add Payment</button>
                              )}
                              <div style={{ borderTop: '1px solid #e2e8f0', margin: '4px 0' }}></div>
                              <button className="dropdown-item" onClick={() => { setOpenActionId(null); handleDeleteExpense(expense); }} style={{ color: '#dc2626' }}><i className="fa fa-trash" style={{ marginRight: '6px' }}></i>Delete</button>
                            </div>,
                            document.body
                          )}
                        </div>
                      </td>
                      <td>{expense.transaction_date || expense.date || 'N/A'}</td>
                      <td>{expense.ref_no || 'N/A'}</td>
                      <td>{expense.category || expense.expense_category || 'N/A'}</td>
                      <td>{expense.location_name || expense.location || 'N/A'}</td>
                      <td>{statusBadge(expense.payment_status)}</td>
                      <td style={{ fontWeight: '600' }}>${parseFloat(expense.final_total || expense.total_amount || 0).toFixed(2)}</td>
                      <td>{getDisplayName(expense.expense_for) || 'N/A'}</td>
                      <td>{getDisplayName(expense.added_by) || 'N/A'}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '1rem', borderTop: '1px solid #e2e8f0', background: 'white' }}>
            <div style={{ fontSize: '0.875rem', color: '#64748b' }}>Showing {fromEntry} to {toEntry} of {total} entries</div>
            <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
              <button className="pagination-btn" onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage <= 1 || loading}>Previous</button>
              {Array.from({ length: Math.min(lastPage, 5) }, (_, i) => {
                const page = lastPage <= 5 ? i + 1 : Math.max(1, currentPage - 2) + i;
                if (page > lastPage) return null;
                return (
                  <button key={page} className="pagination-btn" onClick={() => handlePageChange(page)}
                    style={{ background: page === currentPage ? '#2563eb' : '', color: page === currentPage ? 'white' : '', fontWeight: page === currentPage ? '600' : '' }}>
                    {page}
                  </button>
                );
              })}
              <button className="pagination-btn" onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage >= lastPage || loading}>Next</button>
            </div>
          </div>
        </div>

      {/* Filter Modal */}
      {showFilters && (
        <div className="modal-overlay" onClick={() => setShowFilters(false)}>
          <div className="modal-container filter-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '600px', width: '95%' }}>
            <div className="modal-header">
              <h2>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" style={{ marginRight: '0.5rem', verticalAlign: 'middle' }}>
                  <path d="M10 18h4v-2h-4v2zM3 6v2h18V6H3zm3 7h12v-2H6v2z" />
                </svg>
                Filters
              </h2>
              <button className="modal-close-btn" onClick={() => setShowFilters(false)}>✕</button>
            </div>
            <div className="modal-content" style={{ padding: '1.5rem', maxWidth: 'none' }}>
              <div className="filter-row">
                <div className="filter-group">
                  <label>Business Location:</label>
                  <select value={pendingLocation} onChange={(e) => setPendingLocation(e.target.value)}>
                    <option value="all">All</option>
                    {Array.isArray(locations) && locations.map(loc => <option key={loc.id} value={loc.id}>{loc.name}</option>)}
                  </select>
                </div>
                <div className="filter-group">
                  <label>Expense Category:</label>
                  <select value={pendingCategory} onChange={(e) => setPendingCategory(e.target.value)}>
                    <option value="all">All</option>
                    {Array.isArray(expenseCategories) && expenseCategories.map(cat => <option key={cat.id} value={cat.id}>{cat.name}</option>)}
                  </select>
                </div>
                <div className="filter-group">
                  <label>Payment Status:</label>
                  <select value={pendingPaymentStatus} onChange={(e) => setPendingPaymentStatus(e.target.value)}>
                    <option value="all">All</option>
                    <option value="paid">Paid</option>
                    <option value="due">Due</option>
                    <option value="partial">Partial</option>
                  </select>
                </div>
              </div>
              <div className="filter-row second-row">
                <div className="filter-group">
                  <label>Start Date:</label>
                  <input type="date" value={pendingStartDate} onChange={(e) => setPendingStartDate(e.target.value)} />
                </div>
                <div className="filter-group">
                  <label>End Date:</label>
                  <input type="date" value={pendingEndDate} onChange={(e) => setPendingEndDate(e.target.value)} />
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="modal-btn close-btn" onClick={handleClearFilters}>Clear</button>
              <button className="modal-btn close-btn" onClick={() => setShowFilters(false)}>Cancel</button>
              <button className="modal-btn primary-btn" onClick={handleApplyFilters}>Apply Filters</button>
            </div>
          </div>
        </div>
      )}

      {/* View Expense Modal */}
      {showViewModal && viewExpense && (
        <div className="modal-overlay" onClick={() => setShowViewModal(false)}>
          <div className="modal-container" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '650px', width: '95%', borderRadius: '12px', overflow: 'hidden' }}>
            <div className="modal-header" style={{ background: 'linear-gradient(135deg, #059669 0%, #047857 100%)', color: 'white', padding: '1.25rem 1.5rem' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                <div style={{ width: '40px', height: '40px', borderRadius: '10px', background: 'rgba(255,255,255,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/></svg>
                </div>
                <div>
                  <h2 style={{ margin: 0, fontSize: '1.125rem', fontWeight: '600' }}>Expense Details</h2>
                  <p style={{ margin: '0.15rem 0 0', fontSize: '0.8rem', opacity: 0.85 }}>{viewExpense.ref_no}</p>
                </div>
              </div>
              <button className="modal-close-btn" onClick={() => setShowViewModal(false)} style={{ color: 'white', fontSize: '1.25rem', opacity: 0.8 }}>✕</button>
            </div>
            <div className="modal-content" style={{ padding: '1.5rem', background: '#f8fafc', maxWidth: 'none' }}>
              {(() => {
                const fields = [
                  { label: 'Reference No', value: viewExpense.ref_no },
                  { label: 'Date', value: viewExpense.transaction_date || viewExpense.date || viewExpense.expense_date },
                  { label: 'Category', value: viewExpense.category || viewExpense.expense_category },
                  { label: 'Sub Category', value: viewExpense.sub_category },
                  { label: 'Location', value: viewExpense.location_name || viewExpense.location },
                  { label: 'Expense For', value: getDisplayName(viewExpense.expense_for) },
                  { label: 'Total Amount', value: (viewExpense.final_total || viewExpense.total_amount) ? `$${parseFloat(viewExpense.final_total || viewExpense.total_amount || 0).toFixed(2)}` : null },
                  { label: 'Tax Amount', value: viewExpense.tax_amount ? `$${parseFloat(viewExpense.tax_amount || 0).toFixed(2)}` : null },
                  { label: 'Payment Status', value: viewExpense.payment_status, badge: true },
                  { label: 'Is Refund', value: viewExpense.is_refund ? 'Yes' : 'No' },
                  { label: 'Is Recurring', value: viewExpense.is_recurring ? 'Yes' : 'No' },
                  { label: 'Added By', value: getDisplayName(viewExpense.added_by) },
                ].filter(f => f.value != null && String(f.value).trim() !== '' && String(f.value) !== 'N/A' && String(f.value) !== 'undefined' && String(f.value) !== 'null');
                const hasNotes = viewExpense.additional_notes || viewExpense.expense_note;
                return (
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
                    {fields.map((field, idx) => (
                      <div key={field.label} style={{ background: 'white', borderRadius: '8px', padding: '0.75rem 1rem', border: '1px solid #e2e8f0', gridColumn: (!hasNotes && idx === fields.length - 1 && fields.length % 2 !== 0) ? '1 / -1' : undefined }}>
                        <div style={{ fontSize: '0.7rem', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: '600', marginBottom: '0.3rem' }}>{field.label}</div>
                        <div style={{ fontSize: '0.9rem', color: '#1e293b', fontWeight: '500' }}>
                          {field.badge ? statusBadge(field.value) : field.value}
                        </div>
                      </div>
                    ))}
                    {hasNotes ? (
                      <div style={{ background: 'white', borderRadius: '8px', padding: '0.75rem 1rem', border: '1px solid #e2e8f0', gridColumn: '1 / -1' }}>
                        <div style={{ fontSize: '0.7rem', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: '600', marginBottom: '0.3rem' }}>Notes</div>
                        <div style={{ fontSize: '0.85rem', color: '#475569' }}>{viewExpense.additional_notes || viewExpense.expense_note}</div>
                      </div>
                    ) : null}
                  </div>
                );
              })()}
            </div>
            <div className="modal-footer" style={{ background: 'white', borderTop: '1px solid #e2e8f0', padding: '1rem 1.5rem' }}>
              <button className="modal-btn close-btn" onClick={() => setShowViewModal(false)}>Close</button>
              <button className="modal-btn primary-btn" style={{ background: '#059669' }} onClick={() => { setShowViewModal(false); handleEditClick(viewExpense); }}>Edit Expense</button>
            </div>
          </div>
        </div>
      )}

      {/* View Payments Modal */}
      {showPaymentsModal && paymentTargetExpense && (
        <div className="modal-overlay" onClick={() => setShowPaymentsModal(false)}>
          <div className="modal-container" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '700px', width: '95%', borderRadius: '12px', overflow: 'hidden' }}>
            <div className="modal-header" style={{ background: 'linear-gradient(135deg, #059669 0%, #047857 100%)', color: 'white', padding: '1.25rem 1.5rem' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                <div style={{ width: '40px', height: '40px', borderRadius: '10px', background: 'rgba(255,255,255,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="1" y="4" width="22" height="16" rx="2" ry="2"/><line x1="1" y1="10" x2="23" y2="10"/></svg>
                </div>
                <div>
                  <h2 style={{ margin: 0, fontSize: '1.125rem', fontWeight: '600' }}>Payment History</h2>
                  <p style={{ margin: '0.15rem 0 0', fontSize: '0.8rem', opacity: 0.85 }}>{paymentTargetExpense.ref_no}</p>
                </div>
              </div>
              <button className="modal-close-btn" onClick={() => setShowPaymentsModal(false)} style={{ color: 'white', fontSize: '1.25rem', opacity: 0.8 }}>✕</button>
            </div>
            <div className="modal-content" style={{ padding: '1.5rem', background: '#f8fafc', maxWidth: 'none' }}>
              {/* Summary cards */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '0.75rem', marginBottom: '1.25rem' }}>
                <div style={{ background: 'white', borderRadius: '8px', padding: '0.875rem', border: '1px solid #e2e8f0', textAlign: 'center' }}>
                  <div style={{ fontSize: '0.7rem', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: '600', marginBottom: '0.3rem' }}>Total Amount</div>
                  <div style={{ fontSize: '1.1rem', color: '#1e293b', fontWeight: '700' }}>${parseFloat(paymentTargetExpense.final_total || paymentTargetExpense.total_amount || 0).toFixed(2)}</div>
                </div>
                <div style={{ background: 'white', borderRadius: '8px', padding: '0.875rem', border: '1px solid #e2e8f0', textAlign: 'center' }}>
                  <div style={{ fontSize: '0.7rem', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: '600', marginBottom: '0.3rem' }}>Paid</div>
                  <div style={{ fontSize: '1.1rem', color: '#059669', fontWeight: '700' }}>${paymentsList.reduce((sum, p) => sum + parseFloat(p.amount || 0), 0).toFixed(2)}</div>
                </div>
                <div style={{ background: 'white', borderRadius: '8px', padding: '0.875rem', border: '1px solid #e2e8f0', textAlign: 'center' }}>
                  <div style={{ fontSize: '0.7rem', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: '600', marginBottom: '0.3rem' }}>Due</div>
                  <div style={{ fontSize: '1.1rem', color: '#dc2626', fontWeight: '700' }}>${(parseFloat(paymentTargetExpense.final_total || paymentTargetExpense.total_amount || 0) - paymentsList.reduce((sum, p) => sum + parseFloat(p.amount || 0), 0)).toFixed(2)}</div>
                </div>
              </div>

              {paymentsLoading ? (
                <div style={{ textAlign: 'center', padding: '2rem', color: '#64748b' }}>
                  <div style={{ display: 'inline-block', width: '24px', height: '24px', border: '3px solid #e2e8f0', borderTopColor: '#059669', borderRadius: '50%', animation: 'spin 1s linear infinite' }}></div>
                  <p style={{ marginTop: '0.75rem' }}>Loading payments...</p>
                </div>
              ) : paymentsList.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '2.5rem 1rem', background: 'white', borderRadius: '8px', border: '1px dashed #cbd5e1' }}>
                  <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="1.5" style={{ marginBottom: '0.75rem' }}><rect x="1" y="4" width="22" height="16" rx="2" ry="2"/><line x1="1" y1="10" x2="23" y2="10"/></svg>
                  <p style={{ color: '#64748b', fontSize: '0.95rem', margin: '0 0 0.25rem' }}>No payments found</p>
                  <p style={{ color: '#94a3b8', fontSize: '0.8rem', margin: 0 }}>This expense hasn't been paid yet.</p>
                </div>
              ) : (
                <div style={{ background: 'white', borderRadius: '8px', border: '1px solid #e2e8f0', overflow: 'hidden' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
                    <thead>
                      <tr style={{ background: '#f1f5f9' }}>
                        <th style={{ textAlign: 'left', padding: '0.75rem 1rem', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.05em', color: '#64748b', fontWeight: '600' }}>#</th>
                        <th style={{ textAlign: 'left', padding: '0.75rem 1rem', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.05em', color: '#64748b', fontWeight: '600' }}>Date</th>
                        <th style={{ textAlign: 'left', padding: '0.75rem 1rem', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.05em', color: '#64748b', fontWeight: '600' }}>Method</th>
                        <th style={{ textAlign: 'right', padding: '0.75rem 1rem', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.05em', color: '#64748b', fontWeight: '600' }}>Amount</th>
                        <th style={{ textAlign: 'left', padding: '0.75rem 1rem', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.05em', color: '#64748b', fontWeight: '600' }}>Note</th>
                      </tr>
                    </thead>
                    <tbody>
                      {paymentsList.map((payment, idx) => (
                        <tr key={idx} style={{ borderBottom: '1px solid #f1f5f9', transition: 'background 0.15s' }} onMouseEnter={(e) => e.currentTarget.style.background = '#f8fafc'} onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}>
                          <td style={{ padding: '0.75rem 1rem', color: '#94a3b8', fontSize: '0.8rem' }}>{idx + 1}</td>
                          <td style={{ padding: '0.75rem 1rem', color: '#1e293b', fontWeight: '500' }}>{payment.paid_on || payment.created_at ? new Date(payment.paid_on || payment.created_at).toLocaleDateString() : 'N/A'}</td>
                          <td style={{ padding: '0.75rem 1rem' }}>
                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.3rem', padding: '0.2rem 0.6rem', borderRadius: '9999px', fontSize: '0.75rem', fontWeight: '600', textTransform: 'capitalize', background: payment.method === 'cash' ? '#dcfce7' : payment.method === 'card' ? '#dbeafe' : '#f3f4f6', color: payment.method === 'cash' ? '#166534' : payment.method === 'card' ? '#1e40af' : '#374151' }}>
                              {payment.method || 'N/A'}
                            </span>
                          </td>
                          <td style={{ padding: '0.75rem 1rem', textAlign: 'right', fontWeight: '700', color: '#059669' }}>${parseFloat(payment.amount || 0).toFixed(2)}</td>
                          <td style={{ padding: '0.75rem 1rem', color: '#475569', fontSize: '0.8rem', maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{payment.note || payment.payment_note || '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
            <div className="modal-footer" style={{ background: 'white', borderTop: '1px solid #e2e8f0', padding: '1rem 1.5rem' }}>
              <button className="modal-btn close-btn" onClick={() => setShowPaymentsModal(false)}>Close</button>
              {paymentTargetExpense.payment_status !== 'paid' && (
                <button className="modal-btn primary-btn" style={{ background: '#059669' }} onClick={() => { setShowPaymentsModal(false); handleOpenAddPayment(paymentTargetExpense); }}>Add Payment</button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Add Payment Modal */}
      {showAddPaymentModal && paymentTargetExpense && (
        <div className="modal-overlay" onClick={() => setShowAddPaymentModal(false)}>
          <div className="modal-container" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '500px', width: '95%' }}>
            <div className="modal-header">
              <h2>Add Payment — {paymentTargetExpense.ref_no}</h2>
              <button className="modal-close-btn" onClick={() => setShowAddPaymentModal(false)}>✕</button>
            </div>
            <div className="modal-content" style={{ padding: '1.5rem', maxWidth: 'none' }}>
              <div className="form-row" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                <div className="form-group">
                  <label>Amount: *</label>
                  <input type="number" className="form-input" step="0.01" value={addPaymentForm.amount}
                    onChange={(e) => setAddPaymentForm({ ...addPaymentForm, amount: e.target.value })} required />
                </div>
                <div className="form-group">
                  <label>Method: *</label>
                  <select className="form-select" value={addPaymentForm.method}
                    onChange={(e) => setAddPaymentForm({ ...addPaymentForm, method: e.target.value })}>
                    <option value="cash">Cash</option>
                    <option value="card">Card</option>
                    <option value="cheque">Cheque</option>
                    <option value="bank_transfer">Bank Transfer</option>
                    <option value="other">Other</option>
                  </select>
                </div>
              </div>
              <div className="form-row" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                <div className="form-group">
                  <label>Paid On: *</label>
                  <input type="datetime-local" className="form-input" value={addPaymentForm.paid_on}
                    onChange={(e) => setAddPaymentForm({ ...addPaymentForm, paid_on: e.target.value })} required />
                </div>
                <div className="form-group">
                  <label>Payment Account:</label>
                  <select className="form-select" value={addPaymentForm.payment_account_id}
                    onChange={(e) => setAddPaymentForm({ ...addPaymentForm, payment_account_id: e.target.value })}>
                    <option value="">Select Account</option>
                    {Array.isArray(paymentAccounts) && paymentAccounts.map(acc => (
                      <option key={acc.id} value={acc.id}>{acc.name}</option>
                    ))}
                  </select>
                </div>
              </div>
              {addPaymentForm.method === 'card' && (
                <div className="form-row" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                  <div className="form-group">
                    <label>Card Number:</label>
                    <input type="text" className="form-input" value={addPaymentForm.card_number}
                      onChange={(e) => setAddPaymentForm({ ...addPaymentForm, card_number: e.target.value })} />
                  </div>
                  <div className="form-group">
                    <label>Card Holder:</label>
                    <input type="text" className="form-input" value={addPaymentForm.card_holder_name}
                      onChange={(e) => setAddPaymentForm({ ...addPaymentForm, card_holder_name: e.target.value })} />
                  </div>
                  <div className="form-group">
                    <label>Card Type:</label>
                    <select className="form-select" value={addPaymentForm.card_type}
                      onChange={(e) => setAddPaymentForm({ ...addPaymentForm, card_type: e.target.value })}>
                      <option value="">Select</option>
                      <option value="visa">Visa</option>
                      <option value="mastercard">Mastercard</option>
                      <option value="amex">Amex</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Security Code:</label>
                    <input type="text" className="form-input" value={addPaymentForm.card_security}
                      onChange={(e) => setAddPaymentForm({ ...addPaymentForm, card_security: e.target.value })} />
                  </div>
                </div>
              )}
              {addPaymentForm.method === 'cheque' && (
                <div className="form-row" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                  <div className="form-group">
                    <label>Cheque Number:</label>
                    <input type="text" className="form-input" value={addPaymentForm.cheque_number}
                      onChange={(e) => setAddPaymentForm({ ...addPaymentForm, cheque_number: e.target.value })} />
                  </div>
                  <div className="form-group">
                    <label>Bank Account Number:</label>
                    <input type="text" className="form-input" value={addPaymentForm.bank_account_number}
                      onChange={(e) => setAddPaymentForm({ ...addPaymentForm, bank_account_number: e.target.value })} />
                  </div>
                </div>
              )}
              <div className="form-row">
                <div className="form-group full-width">
                  <label>Note:</label>
                  <textarea className="form-textarea" rows="2" value={addPaymentForm.note}
                    onChange={(e) => setAddPaymentForm({ ...addPaymentForm, note: e.target.value })} />
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="modal-btn close-btn" onClick={() => setShowAddPaymentModal(false)} disabled={isSaving}>Cancel</button>
              <button className="modal-btn primary-btn" onClick={handleSavePayment} disabled={isSaving}>
                {isSaving ? '⏳ Saving...' : 'Save Payment'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* View Document Modal */}
      {showDocModal && docUrl && (
        <div className="modal-overlay" onClick={() => setShowDocModal(false)}>
          <div className="modal-container" onClick={(e) => e.stopPropagation()} style={{ maxWidth: docIsImage ? '800px' : '600px', width: '95%' }}>
            <div className="modal-header">
              <h2>Document</h2>
              <button className="modal-close-btn" onClick={() => setShowDocModal(false)}>✕</button>
            </div>
            <div className="modal-content" style={{ padding: '1.5rem', textAlign: 'center', maxWidth: 'none' }}>
              {docIsImage ? (
                <img src={docUrl} alt="Document" style={{ maxWidth: '100%', maxHeight: '70vh', borderRadius: '8px' }} />
              ) : (
                <div>
                  <p>Preview not available for this file type.</p>
                  <a href={docUrl} download className="modal-btn primary-btn" style={{ display: 'inline-block', marginTop: '1rem', textDecoration: 'none' }}>
                    Download Document
                  </a>
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button className="modal-btn close-btn" onClick={() => setShowDocModal(false)}>Close</button>
              <a href={docUrl} download className="modal-btn primary-btn" style={{ textDecoration: 'none', display: 'inline-block' }}>Download</a>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Expenses;
